import { useEffect, useState, useCallback } from "react";
import { Loader2, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money, StatusBadge, OVERLINE, H1, Panel, btnPrimary, useAdmin } from "@/components/admin/ui";

const SUB_STATUSES = ["active", "trial", "past_due", "cancelled", "comped", "inactive"];

function PlanEditor({ plans, canEdit, onSaved }) {
  const [draft, setDraft] = useState(plans);
  const [busy, setBusy] = useState(null);
  const save = async (key) => {
    setBusy(key);
    try { await api.adminUpdatePlan(key, { price_cents: Math.round(Number(draft[key].price_cents)), fee_pct: Number(draft[key].fee_pct) }); toast.success(`${draft[key].name} saved`); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  return (
    <Panel title="Subscription plans (configurable)" className="mt-6">
      <div className="grid sm:grid-cols-3 gap-3" data-testid="plan-editor">
        {Object.entries(draft).map(([key, p]) => (
          <div key={key} className="border border-[#E5E2DC] p-4" data-testid={`plan-${key}`}>
            <p className="font-serif text-lg text-[#1F2A22]">{p.name}</p>
            <label className="block text-xs text-[#5C6B61] mt-2">Price (¢/mo)
              <input type="number" disabled={!canEdit} value={p.price_cents} onChange={(e) => setDraft({ ...draft, [key]: { ...p, price_cents: e.target.value } })} className="w-full border border-[#E5E2DC] bg-white px-2 py-1.5 mt-1 text-sm outline-none focus:border-[#1F2A22] disabled:opacity-60" data-testid={`plan-price-${key}`} /></label>
            <label className="block text-xs text-[#5C6B61] mt-2">Fee (%)
              <input type="number" step="0.1" disabled={!canEdit} value={p.fee_pct} onChange={(e) => setDraft({ ...draft, [key]: { ...p, fee_pct: e.target.value } })} className="w-full border border-[#E5E2DC] bg-white px-2 py-1.5 mt-1 text-sm outline-none focus:border-[#1F2A22] disabled:opacity-60" data-testid={`plan-fee-${key}`} /></label>
            {canEdit && <button onClick={() => save(key)} disabled={busy === key} className={`${btnPrimary} w-full mt-3 py-1.5 text-xs`} data-testid={`plan-save-${key}`}>{busy === key ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</button>}
          </div>
        ))}
      </div>
      {!canEdit && <p className="text-xs text-[#8A5A44] mt-3">Only Owners can change plan pricing.</p>}
    </Panel>
  );
}

export default function AdminSubscriptions() {
  const { admin } = useAdmin();
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.adminSubscriptions().then(setData).catch(() => setData(null)); }, []);
  useEffect(() => { load(); }, [load]);

  const changePlan = async (id, plan) => { try { await api.adminSubscriptionAction(id, { action: "change_plan", plan }); toast.success("Plan changed"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const setStatus = async (id, status) => { try { await api.adminSubscriptionAction(id, { action: "set_status", status }); toast.success("Status updated"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-6 md:p-8" data-testid="admin-subscriptions">
      <span className={OVERLINE}>Commerce</span>
      <h1 className={`${H1} mt-1`}>Subscriptions</h1>

      {!data ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div> : (
        <>
          <div className="flex flex-wrap gap-4 mt-4 text-sm">
            {SUB_STATUSES.map((s) => <span key={s} className="text-[#5C6B61]">{s}: <strong className="text-[#1F2A22]">{data.counts[s] || 0}</strong></span>)}
          </div>

          <div className="mt-4 bg-white border border-[#E5E2DC] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F2F0EB] text-[#5C6B61] uppercase tracking-wide text-[11px]">
                <tr>{["Business", "Plan", "Price", "Status", "Lifetime Rev", "Actions"].map((h) => <th key={h} className="text-left font-serif p-3 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.subscriptions.map((s) => (
                  <tr key={s.id} className="border-t border-[#E5E2DC]" data-testid="subscription-row">
                    <td className="p-3 font-medium text-[#1F2A22] whitespace-nowrap">{s.name}<span className="block text-xs text-[#5C6B61] font-normal">{s.contact_email}</span></td>
                    <td className="p-3">
                      <select value={s.plan || ""} onChange={(e) => changePlan(s.id, e.target.value)} className="border border-[#E5E2DC] bg-white px-2 py-1 text-sm" data-testid="sub-plan-select">
                        <option value="">—</option>{Object.keys(data.plans).map((k) => <option key={k} value={k}>{data.plans[k].name}</option>)}
                      </select>
                    </td>
                    <td className="p-3 tabular-nums whitespace-nowrap">{s.price_cents ? money(s.price_cents) + "/mo" : "—"}</td>
                    <td className="p-3">
                      <select value={s.subscription_status} onChange={(e) => setStatus(s.id, e.target.value)} className="border border-[#E5E2DC] bg-white px-2 py-1 text-sm" data-testid="sub-status-select">
                        {SUB_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </td>
                    <td className="p-3 tabular-nums text-[#8A5A44] whitespace-nowrap">{money(s.lifetime_revenue_cents)}</td>
                    <td className="p-3">
                      <button onClick={() => setStatus(s.id, "comped")} className="text-[11px] font-semibold px-2 py-1 border border-[#1F2A22]/30 text-[#1F2A22] hover:bg-[#1F2A22] hover:text-white" data-testid="sub-comp">Comp</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PlanEditor plans={data.plans} canEdit={admin?.permissions?.includes("fees_config")} onSaved={load} />
        </>
      )}
    </div>
  );
}
